import { col, type Shape } from './base.ts';

/**
 * Identity / lifecycle scalars of an installed ESP completion (`ESP_Pump_Details`):
 * supply + status, the run-life dates, and the pull/failure narrative. Bare
 * source columns (`suffix === canonical`) except `stopOrFailureDate`, whose
 * source carries the load-bearing `OR` casing (`stopORfailureDate`). The dates
 * are typed from the source schema (timestamps); the rest are text/number
 * nameplate scalars — all dimensionless, so `unitless`.
 */
export const ESP_INSTALLATION: Shape = [
  col('status', 'unitless', 'Current status of this ESP installation.'),
  col('supplyCompany', 'unitless', 'Company that supplied the ESP equipment.'),
  col('espComponent', 'unitless', 'ESP component implicated in the stop/failure event.'),
  col('adcoInstallation', 'unitless', 'ADCO installation identifier for this ESP.'),
  col('wellInstallationNumber', 'unitless', 'Sequential installation number of this ESP on the well string.'),
  col('commissionDate', 'unitless', 'Date the ESP installation was commissioned.'),
  col('runDate', 'unitless', 'Date the ESP was run in hole / started.'),
  col('stopOrFailureDate', 'unitless', 'Date the ESP stopped or failed.', { suffix: 'stopORfailureDate' }),
  col('pullDate', 'unitless', 'Date the ESP was pulled from the well.'),
  col('pullReason', 'unitless', 'Reason the ESP was pulled.'),
  col('rootCause', 'unitless', 'Diagnosed root cause of the stop/failure.'),
  col('comments', 'unitless', 'Free-text remarks on this ESP installation.'),
];

/**
 * Setting depths of the ESP completion, in feet. The source spells the datum on
 * each column (`_MD`/`_TVD`, the sensor pair also carrying a `_ft` tail); the
 * canonical drops the underscores to camelCase. Both measured- and true-vertical
 * depths are `length`.
 */
export const ESP_DEPTHS: Shape = [
  col('sensorDepthMdFt', 'length', 'ESP gauge/sensor setting depth (measured depth, ft).', {
    suffix: 'espSensorDepth_MD_ft',
  }),
  col('sensorDepthTvdFt', 'length', 'ESP gauge/sensor setting depth (true vertical depth, ft).', {
    suffix: 'espSensorDepth_TVD_ft',
  }),
  col('tolMd', 'length', 'Top-of-liner setting depth (measured depth).', { suffix: 'tol_MD' }),
  col('tolTvd', 'length', 'Top-of-liner setting depth (true vertical depth).', { suffix: 'tol_TVD' }),
  col('topReservoirMd', 'length', 'Top-of-reservoir depth (measured depth).', { suffix: 'topReservoirDepth_MD' }),
  col('topReservoirTvd', 'length', 'Top-of-reservoir depth (true vertical depth).', {
    suffix: 'topReservoirDepth_TVD',
  }),
];

/**
 * The downhole ESP equipment tally as one struct per component, bottom-to-top
 * along the string (motor → protector → intake → pump → AGH → BOH → MLE →
 * cable → monitor). Each struct collapses the component's repeated source prefix
 * (`pump*`, `motor*`, …) into bare members — so the duplicated `series` / `matl`
 * / `nORu` (new-or-used) attributes no longer collide on a flat surface. The
 * member engine types are derived from the source schema; the struct `kind` is
 * `unitless` because each block mixes identifiers, materials and a few
 * power/voltage/current numerics that share no single unit.
 */
export const ESP_COMPONENTS: Shape = [
  {
    canonical: 'pump',
    members: {
      series: 'pumpSeries',
      model: 'pumpModel',
      housing: 'pumpHsg',
      totalStages: 'totalStages',
      ar: 'pumpAr',
      material: 'pumpMatl',
      newOrUsed: 'pump_nORu',
    },
    description: 'Pump nameplate: series, model, housing, total stages, AR, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'motor',
    members: {
      series: 'motorSeries',
      poles: 'numberOfPoles',
      housing: 'motorHsg',
      totalPower: 'motorTotalPower',
      totalVoltage: 'motorTotalV',
      current: 'motorA',
      referenceWatts: 'motorRefW',
      material: 'motorMatl',
      newOrUsed: 'motor_nORu',
    },
    description:
      'Motor nameplate: series, poles, housing, total power/voltage/current, reference watts, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'protector',
    members: {
      series: 'protectorSeries',
      model: 'protectorModel',
      material: 'protectorMatl',
      newOrUsed: 'protector_nORu',
    },
    description: 'Protector (seal) nameplate: series, model, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'intake',
    members: {
      series: 'intakeVgsaSeries',
      model: 'intakeVgsaModel',
      material: 'intakeVgsaMatl',
      newOrUsed: 'intakeVgsa_nORu',
    },
    description: 'Intake / vortex gas separator (VGSA) nameplate: series, model, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'agh',
    members: { model: 'aghModel', material: 'aghMatl', newOrUsed: 'agh_nORu' },
    description: 'Advanced gas handler (AGH) nameplate: model, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'boh',
    members: { series: 'bohSeries', thread: 'bohThd', material: 'bohMatl', newOrUsed: 'nORu' },
    description: 'Bottom-of-hole (BOH) nameplate: series, thread, material and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'mle',
    members: { series: 'mleSeries', type: 'mleType', length: 'mle' },
    description: 'Motor lead extension (MLE) nameplate: series, type and length.',
    kind: 'unitless',
  },
  {
    canonical: 'cable',
    members: { type: 'cableType', size: 'cableSize', totalLength: 'cableTotalLength', newOrUsed: 'cable_nORu' },
    description: 'Power cable nameplate: type, size, total length and new-or-used flag.',
    kind: 'unitless',
  },
  {
    canonical: 'monitor',
    members: { type: 'monitorType', newOrUsed: 'monitor_nORu' },
    description: 'Downhole monitor (gauge) nameplate: type and new-or-used flag.',
    kind: 'unitless',
  },
];

/**
 * The surface power-delivery gear of the ESP installation: the variable-speed
 * drive (`panelVsd*` → `vsd`) and the step-up transformer, each as a struct with
 * its prefix stripped, plus the two bare completion accessories (`yTool`,
 * `packer`). Same `unitless` rationale as {@link ESP_COMPONENTS} — member types
 * come from the source schema.
 */
export const ESP_SURFACE: Shape = [
  {
    canonical: 'vsd',
    members: { type: 'panelVsdType', kva: 'panelVsdKVA', current: 'panelVsdA' },
    description: 'Variable-speed drive (VSD) panel nameplate: type, apparent power (KVA) and current (A).',
    kind: 'unitless',
  },
  {
    canonical: 'transformer',
    members: {
      kva: 'transformerKVA',
      current: 'transformerA',
      tapSetting: 'transformerTapSetting',
      voltageRatio: 'transformerVoltageRatio',
      comments: 'transformerComments',
    },
    description:
      'Step-up transformer nameplate: apparent power (KVA), current (A), tap setting, voltage ratio and comments.',
    kind: 'unitless',
  },
  col('yTool', 'unitless', 'Y-tool present / type on the completion.'),
  col('packer', 'unitless', 'Packer present / type on the completion.'),
];
